import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { UiBadge, UiDatePicker, UiListSearch, UiPanel, UiSortMenuBadge } from '@/components';
import { can } from '@/composables/useAuth';
import { AuraService } from '@/services';
import { toastServiceError } from '@/services/core/notification';
import { useAuraProcessingCostsStore, useUiStore } from '@/stores';
import { Permission } from '@/types';
import { normalizeUtcDateRange, startOfUtcDay } from '@/utils/datetime';
import type { AuraProcessingCost, AuraProcessingCostsFilters, SortMenuItem } from '@/types';

import AuraProcessingCostsHeader from './components/AuraProcessingCostsHeader.vue';
import AuraProcessingCostsFiltersPanel from './components/AuraProcessingCostsFilters.vue';
import AuraProcessingCostsSort from './components/AuraProcessingCostsSort.vue';
import AuraProcessingCostsSummary from './components/AuraProcessingCostsSummary.vue';
import AuraProcessingCostsTable from './components/AuraProcessingCostsTable.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraProcessingCosts',
  setup(__props, { expose: __expose }) {
  __expose();

const items = ref<AuraProcessingCost[]>([]);
const uiStore = useUiStore();
const processingCostsStore = useAuraProcessingCostsStore();
const isLoading = computed(() => uiStore.isLoading);
const total = ref(0);
const totalCost = ref(0);
const totalTokens = ref(0);
const filters = computed({
    get: () => processingCostsStore.filters,
    set: (value: AuraProcessingCostsFilters) => processingCostsStore.setFilters(value),
});
const sortMenuSelected = computed({
    get: () => processingCostsStore.sortMenuSelected,
    set: (value: SortMenuItem[]) => processingCostsStore.setSortMenuSelected(value),
});
const users = ref<Array<{ id: number; name: string }>>([]);
const activatedAt = ref<string | null>(null);
const { t, te } = useI18n();

const canView = computed(() => can(Permission.VIEW_AURA_PROCESSING_COSTS));
const orderBy = computed(() =>
    sortMenuSelected.value.map((item) => `${item.direction}${item.key}`).join(','),
);
const search = computed({
    get: () => filters.value.search || '',
    set: (value: string) => {
        filters.value = { ...filters.value, search: value || undefined, page: 1 };
    },
});
const activatedMinDate = computed(() => {
    if (!activatedAt.value) return undefined;
    return startOfUtcDay(activatedAt.value);
});
const translateProcessingType = (processingType: string): string => {
    const key = `aura.processingCosts.processingTypes.${processingType}`;
    return te(key) ? t(key) : t('aura.processingCosts.processingTypes.unknown');
};
const translateStatus = (status: string): string => {
    const key = `aura.processingCosts.statuses.${status}`;
    return te(key) ? t(key) : t('aura.processingCosts.statuses.unknown');
};
const dateRange = computed<Array<Date>>({
    get: () => [
        new Date(filters.value.dateFrom || Date.now()),
        new Date(filters.value.dateTo || Date.now()),
    ],
    set: (range) => {
        if (range.length < 2) return;
        const [dateFrom, dateTo] = range;
        const [normalizedFrom, normalizedTo] = normalizeUtcDateRange(dateFrom, dateTo);
        filters.value = {
            ...filters.value,
            dateFrom: normalizedFrom.toISOString(),
            dateTo: normalizedTo.toISOString(),
            page: 1,
        };
    },
});
const activeFilters = computed(() => {
    const items: Array<{ field: keyof AuraProcessingCostsFilters; label: string; value: string }> =
        [];
    if (filters.value.search)
        items.push({
            field: 'search',
            label: t('aura.processingCosts.filters.searchLabel'),
            value: filters.value.search,
        });
    if (filters.value.userId) {
        const user = users.value.find(({ id }) => id === filters.value.userId);
        if (user)
            items.push({
                field: 'userId',
                label: t('aura.processingCosts.filters.userLabel'),
                value: user.name,
            });
    }
    if (filters.value.processingType)
        items.push({
            field: 'processingType',
            label: t('aura.processingCosts.filters.typeLabel'),
            value: translateProcessingType(filters.value.processingType),
        });
    if (filters.value.status)
        items.push({
            field: 'status',
            label: t('aura.processingCosts.filters.statusLabel'),
            value: translateStatus(filters.value.status),
        });
    return items;
});

const loadCosts = async () => {
    if (!canView.value) return;

    uiStore.setIsLoading(true);

    try {
        const response = await AuraService.getProcessingCosts({
            ...filters.value,
            sort: orderBy.value,
        });
        items.value = response.data.items;
        total.value = response.data.total;
        totalCost.value = response.data.totalCost;
        totalTokens.value = response.data.totalTokens;
        users.value = response.data.users;
        activatedAt.value = response.data.activatedAt;
    } catch (error) {
        toastServiceError(error);
    } finally {
        uiStore.setIsLoading(false);
    }
};

const formatCost = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 6,
    }).format(value);

const formatDate = (value: string) =>
    new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const removeFilter = (field: keyof AuraProcessingCostsFilters) => {
    filters.value = { ...filters.value, [field]: undefined, page: 1 };
};

const removeSort = (item: SortMenuItem) => {
    sortMenuSelected.value = sortMenuSelected.value.filter(({ key }) => key !== item.key);
};

onMounted(loadCosts);
watch([filters, sortMenuSelected], () => loadCosts(), { deep: true });

const __returned__ = { items, uiStore, processingCostsStore, isLoading, total, totalCost, totalTokens, filters, sortMenuSelected, users, activatedAt, t, te, canView, orderBy, search, activatedMinDate, translateProcessingType, translateStatus, dateRange, activeFilters, loadCosts, formatCost, formatDate, removeFilter, removeSort, get UiBadge() { return UiBadge }, get UiDatePicker() { return UiDatePicker }, get UiListSearch() { return UiListSearch }, get UiPanel() { return UiPanel }, get UiSortMenuBadge() { return UiSortMenuBadge }, AuraProcessingCostsHeader, AuraProcessingCostsFiltersPanel, AuraProcessingCostsSort, AuraProcessingCostsSummary, AuraProcessingCostsTable }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})